import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Paperclip, PaperPlaneTilt, Plus } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { inr, fmtDateTime, priorityBadge, requestStatusBadge, clientReqBadge } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";

const TYPES = ["Client Related", "KYC", "Portfolio", "Transaction", "Withdrawal",
  "Document", "Technical Issue", "Account Access", "Other"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const CLIENT_STATUSES = ["Pending", "In Review", "Approved", "Rejected", "Completed"];

export default function WrmRequests() {
  const [rows, setRows] = useState(null);
  const [clientReqs, setClientReqs] = useState([]);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState("");
  const [dlg, setDlg] = useState(false);
  const [sel, setSel] = useState(null);
  const [csel, setCsel] = useState(null);
  const [cform, setCform] = useState({ status: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ subject: "", type: "Client Related", priority: "Medium", client_id: "", description: "", attachment: null });

  const load = () => {
    api.get("/wrm/requests").then((r) => setRows(r.data)).catch((e) => setError(apiError(e)));
    api.get("/wrm/client-requests").then((r) => setClientReqs(r.data)).catch(() => {});
    api.get("/wrm/overview").then((r) => setClients(r.data.clients)).catch(() => {});
  };
  useEffect(load, []);

  const openClientReq = (r) => { setCsel(r); setCform({ status: r.status, note: "" }); };

  const updateClientReq = async () => {
    setBusy(true);
    try {
      await api.patch(`/wrm/client-requests/${csel.id}`, { status: cform.status, note: cform.note || null });
      toast.success(`Request ${csel.request_code} updated — client notified.`);
      setCsel(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error("Subject and description are required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("subject", form.subject.trim());
      fd.append("type", form.type);
      fd.append("priority", form.priority);
      fd.append("description", form.description.trim());
      if (form.client_id) fd.append("client_id", form.client_id);
      if (form.attachment) fd.append("attachment", form.attachment);
      const r = await api.post("/wrm/requests", fd);
      toast.success(`Request ${r.data.request_code} raised — Admin notified`);
      setDlg(false);
      setForm({ subject: "", type: "Client Related", priority: "Medium", client_id: "", description: "", attachment: null });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadAttachment = async (r) => {
    try {
      const res = await api.get(`/requests/${r.id}/attachment`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = r.attachment_name || "attachment";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <AppLayout title="Requests"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!rows) return <AppLayout title="Requests"><PageLoader /></AppLayout>;

  return (
    <AppLayout title="Requests" subtitle="Raise requests to KMC Administration and track their status.">
      <div className="fade-up space-y-5">
        <div className="flex justify-end">
          <Button data-testid="raise-request-button" onClick={() => setDlg(true)}
            className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold emerald-glow">
            <Plus size={16} className="mr-1.5" weight="bold" /> Raise Request
          </Button>
        </div>

        <div className="glass-card overflow-hidden">
          {rows.length === 0 ? <EmptyState message="No requests raised yet." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="wrm-requests-table">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className={thCls}>Request ID</th>
                    <th className={thCls}>Type</th>
                    <th className={thCls}>Priority</th>
                    <th className={thCls}>Client</th>
                    <th className={thCls}>Subject</th>
                    <th className={thCls}>Status</th>
                    <th className={thCls}>Raised</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} onClick={() => setSel(r)} data-testid={`wrm-request-row-${r.id}`}
                      className={`${rowCls} cursor-pointer`}>
                      <td className={`${tdCls} tnum text-xs text-emerald-300`}>{r.request_code}</td>
                      <td className={`${tdCls} text-xs text-zinc-300`}>{r.type}</td>
                      <td className={tdCls}><Badge variant="outline" className={`text-[10px] ${priorityBadge(r.priority)}`}>{r.priority}</Badge></td>
                      <td className={`${tdCls} text-xs text-zinc-400`}>{r.client_name || "—"}</td>
                      <td className={`${tdCls} text-xs text-zinc-300 max-w-[220px] truncate`}>{r.subject}</td>
                      <td className={tdCls}><Badge variant="outline" className={`text-[10px] ${requestStatusBadge(r.status)}`} data-testid={`wrm-req-status-${r.id}`}>{r.status}</Badge></td>
                      <td className={`${tdCls} tnum text-[11px] text-zinc-500 whitespace-nowrap`}>{fmtDateTime(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-3" data-testid="client-requests-section">
          <div>
            <h3 className="font-heading font-semibold text-white">Client Requests</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Requests raised by your assigned clients — review and update status.</p>
          </div>
          <div className="glass-card overflow-hidden">
            {clientReqs.length === 0 ? <EmptyState message="No client requests yet." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="client-requests-table">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className={thCls}>Request ID</th>
                      <th className={thCls}>Client</th>
                      <th className={thCls}>Request Type</th>
                      <th className={thCls}>Subject</th>
                      <th className={`${thCls} text-right`}>Amount</th>
                      <th className={thCls}>Status</th>
                      <th className={thCls}>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientReqs.map((r) => (
                      <tr key={r.id} onClick={() => openClientReq(r)} data-testid={`client-request-row-${r.id}`}
                        className={`${rowCls} cursor-pointer`}>
                        <td className={`${tdCls} tnum text-xs text-emerald-300`}>{r.request_code}</td>
                        <td className={`${tdCls} text-xs text-zinc-200`}>{r.investor_name}</td>
                        <td className={`${tdCls} text-xs text-zinc-300`}>{r.type}</td>
                        <td className={`${tdCls} text-xs text-zinc-300 max-w-[200px] truncate`}>{r.subject}</td>
                        <td className={`${tdCls} text-right tnum text-zinc-100`}>{r.requested_amount != null ? inr(r.requested_amount) : "—"}</td>
                        <td className={tdCls}><Badge variant="outline" className={`text-[10px] ${clientReqBadge(r.status)}`} data-testid={`client-req-status-${r.id}`}>{r.status}</Badge></td>
                        <td className={`${tdCls} tnum text-[11px] text-zinc-500 whitespace-nowrap`}>{fmtDateTime(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-xl" data-testid="raise-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">Raise Request</DialogTitle>
            <DialogDescription className="text-zinc-500">A unique Request ID will be generated and Admin notified immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="label-caps">Request Type</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="rr-type"
                  className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="label-caps">Priority</label>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="rr-priority"
                  className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="label-caps">Client (optional)</label>
              <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="rr-client"
                className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
                <option value="">Not client-specific</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name} ({c.investor_code})</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="label-caps">Subject</label>
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                data-testid="rr-subject" className="glass-input" placeholder="Brief summary" />
            </div>
            <div className="space-y-1.5">
              <label className="label-caps">Description</label>
              <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="rr-description" className="glass-input h-auto py-2.5 resize-none" placeholder="Detailed description..." />
            </div>
            <div className="space-y-1.5">
              <label className="label-caps">Attachment (optional · pdf/image, max 10 MB)</label>
              <label className="glass-input h-auto py-2.5 flex items-center gap-2 cursor-pointer text-zinc-400 text-sm" data-testid="rr-attachment-label">
                <Paperclip size={15} />
                {form.attachment ? form.attachment.name : "Choose file..."}
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="rr-attachment"
                  onChange={(e) => setForm({ ...form, attachment: e.target.files?.[0] || null })} />
              </label>
            </div>
            <Button onClick={submit} disabled={busy} data-testid="rr-submit"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
              <PaperPlaneTilt size={15} className="mr-1.5" weight="bold" /> {busy ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-xl max-h-[90vh] overflow-y-auto" data-testid="wrm-request-detail">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-white flex items-center gap-3 flex-wrap">
                  {sel.request_code}
                  <Badge variant="outline" className={`text-[10px] ${priorityBadge(sel.priority)}`}>{sel.priority}</Badge>
                  <Badge variant="outline" className={`text-[10px] ${requestStatusBadge(sel.status)}`}>{sel.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-white font-medium">{sel.subject}</p>
                  <p className="text-xs text-zinc-500 mt-1">{sel.type} · {fmtDateTime(sel.created_at)}{sel.client_name ? ` · ${sel.client_name}` : ""}</p>
                  <p className="text-sm text-zinc-300 mt-3 whitespace-pre-wrap">{sel.description}</p>
                </div>
                {sel.has_attachment && (
                  <Button size="sm" variant="outline" onClick={() => downloadAttachment(sel)} data-testid="wrm-req-attachment"
                    className="border-white/10 text-zinc-300 hover:bg-white/5">
                    <Download size={13} className="mr-1.5" /> {sel.attachment_name || "Attachment"}
                  </Button>
                )}
                <div>
                  <p className="label-caps mb-2">Admin Responses</p>
                  <div className="space-y-2.5" data-testid="wrm-req-thread">
                    {(sel.thread || []).length === 0 && <p className="text-xs text-zinc-600">No responses yet.</p>}
                    {(sel.thread || []).map((t) => (
                      <div key={t.id} className="border border-white/[0.06] bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-emerald-300">{t.by}</p>
                          <p className="text-[10px] text-zinc-600 tnum">{fmtDateTime(t.at)}</p>
                        </div>
                        <p className="text-sm text-zinc-300 mt-1.5">{t.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!csel} onOpenChange={() => setCsel(null)}>
        <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-xl" data-testid="client-request-detail">
          {csel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-white flex items-center gap-3 flex-wrap">
                  {csel.request_code}
                  <Badge variant="outline" className={`text-[10px] ${clientReqBadge(csel.status)}`}>{csel.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-white font-medium" data-testid="creq-subject">{csel.subject}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {csel.investor_name} ({csel.investor_code}) · {csel.type} · {fmtDateTime(csel.created_at)}
                  </p>
                  {csel.requested_amount != null && (
                    <p className="text-sm text-emerald-300 tnum mt-2" data-testid="creq-amount">Requested Amount: {inr(csel.requested_amount)}</p>
                  )}
                  <p className="text-sm text-zinc-300 mt-3 whitespace-pre-wrap">{csel.description}</p>
                </div>
                {(csel.thread || []).length > 0 && (
                  <div className="space-y-2.5" data-testid="creq-thread">
                    {csel.thread.map((t) => (
                      <div key={t.id} className="border border-white/[0.06] bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-emerald-300">{t.by}</p>
                          <p className="text-[10px] text-zinc-600 tnum">{fmtDateTime(t.at)}</p>
                        </div>
                        <p className="text-sm text-zinc-300 mt-1.5">{t.message}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-white/[0.06]">
                  <div className="space-y-1.5">
                    <label className="label-caps">Update Status</label>
                    <select value={cform.status} onChange={(e) => setCform({ ...cform, status: e.target.value })}
                      data-testid="creq-status-select"
                      className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
                      {CLIENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="label-caps">Response Note (optional)</label>
                    <input value={cform.note} onChange={(e) => setCform({ ...cform, note: e.target.value })}
                      data-testid="creq-note" className="glass-input" placeholder="Note visible to client..." />
                  </div>
                </div>
                <Button onClick={updateClientReq} disabled={busy} data-testid="creq-save"
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
                  {busy ? "Saving..." : "Update Request"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
